/**
 * ArchitectureShowcase — the transparency differentiator, in the exhibit system.
 * The claim pipeline is a ruled ledger strip (no rounded cards, no arrow glyphs;
 * numerals + rules carry the sequence). The "20-engine DAG" node is the one
 * orange atom besides the eyebrow — flat orange TEXT, same geometry as its
 * neighbors. No public link to the architecture system: that map is confidential
 * (admin / NDA'd counsel + investors only), NOT a public self-serve page — so the
 * copy promises reproducibility and defensibility, never "browse it here." Atoms:
 * eyebrow / DAG node.
 */

import { Provenance, SectionHeader } from './sectionPrimitives';

const HAIR = { borderColor: 'var(--color-hairline)' } as const;

const FLOW: Array<{ n: string; label: string; sub: string; accent?: boolean }> = [
  { n: '01', label: 'Documents', sub: 'P&L · tax · leases' },
  { n: '02', label: 'Canonical data', sub: 'OCR · classify' },
  { n: '03', label: '20-engine DAG', sub: 'deterministic math', accent: true },
  { n: '04', label: 'Sworn number', sub: 'traced + attested' },
  { n: '05', label: 'Deliverables', sub: 'court-ready PDF' },
];

export default function ArchitectureShowcase() {
  return (
    <section id="architecture" className="scroll-mt-24 border-t border-hairline">
      <div className="mx-auto max-w-[1400px] px-5 py-16 sm:px-8 lg:px-12 lg:py-24">
        <SectionHeader
          eyebrow="The method"
          title="Every number can be traced back to how it was built"
          lede="Most platforms ask you to trust a black box. Here, every engine, every data flow, and every decision is versioned, reproducible, and documented — so the method stands up to an opposing expert, not just a demo."
        />

        {/* Claim pipeline — a ruled ledger strip */}
        <div className="mt-12 border" style={HAIR}>
          <div
            className="border-b px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.18em] text-text-dim"
            style={HAIR}
          >
            Claim pipeline — deterministic
          </div>
          <ol className="grid divide-y divide-hairline md:grid-cols-5 md:divide-x md:divide-y-0">
            {FLOW.map((n) => (
              <li key={n.label} className="p-5">
                <span className="font-mono text-[11px] text-text-dim">{n.n}</span>
                <h3
                  className={`mt-3 font-heading text-lg leading-snug tracking-tight ${
                    n.accent ? 'text-brand-text' : 'text-text-primary'
                  }`}
                >
                  {n.label}
                </h3>
                <p className="mt-1 font-mono text-[11px] text-text-dim">{n.sub}</p>
              </li>
            ))}
          </ol>
        </div>

        <Provenance note="Every engine versioned · every run reproducible · full method available to counsel in discovery" />
      </div>
    </section>
  );
}
